import React, { FC, useState, useEffect, useRef } from 'react';

import { CarouselProvider, Slider, Slide, Dot } from 'pure-react-carousel';

import CloseIcon from '@mui/icons-material/Close';
import { Stack, Box, Modal, IconButton } from '@mui/material';

import { useIsMobile } from '@shared/hooks';
import RetryImg from '@shared/components/RetryImg';

import styles from './example-image-carousel.module.scss';

import 'pure-react-carousel/dist/react-carousel.es.css';

interface ExampleImagesCarouselProps {
  imageUrls: string[];
}

const ExampleImagesCarousel: FC<ExampleImagesCarouselProps> = ({
  imageUrls,
}) => {
  const isMobile = useIsMobile();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imagesPerSlide, setImagesPerSlide] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Используем прямые URL'ы (как в конструкторах)
  const directImageUrls = imageUrls;

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  // Определяем количество картинок в слайде в зависимости от размера экрана
  useEffect(() => {
    const updateImagesPerSlide = () => {
      if (!isMobile) {
        setImagesPerSlide(1); // На десктопе всегда одна картинка
        return;
      }

      const screenWidth = window.innerWidth;
      
      // Если экран достаточно широкий для двух картинок (с учетом отступов)
      if (screenWidth >= 480) {
        setImagesPerSlide(2);
      } else {
        setImagesPerSlide(1);
      }
    };

    updateImagesPerSlide();
    window.addEventListener('resize', updateImagesPerSlide);
    
    return () => {
      window.removeEventListener('resize', updateImagesPerSlide);
    };
  }, [isMobile]);

  const renderCarousel = () => {
    // Создаем слайды в зависимости от количества картинок на слайд
    const slides = [];
    for (let i = 0; i < directImageUrls.length; i += imagesPerSlide) {
      slides.push(directImageUrls.slice(i, i + imagesPerSlide));
    }

    return (
      <CarouselProvider
        naturalSlideWidth={300}
        naturalSlideHeight={200}
        totalSlides={slides.length}
      >
        <Slider 
          className={styles.slider}
          style={!isMobile ? {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%'
          } : undefined}
        >
          {slides.map((slideUrls, slideIndex) => (
            <Slide 
              key={slideIndex} 
              className={styles.galleryImageSlide} 
              index={slideIndex}
              style={!isMobile ? {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%'
              } : undefined}
            >
              <div 
                className={styles.imageWrapper} 
                ref={containerRef}
                style={!isMobile ? {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  width: '100%'
                } : undefined}
              >
                <Stack 
                  direction="row" 
                  spacing={1} 
                  justifyContent="center" 
                  alignItems="center"
                  sx={{ 
                    gap: '8px',
                    height: '100%',
                    minHeight: '220px',
                    padding: '16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%'
                  }}
                >
                  {slideUrls.map((url, index) => (
                    <Box 
                      key={`${slideIndex}-${index}-${url}`} 
                      className={styles.stackImage} 
                      sx={{ 
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent'
                      }}
                    >
                      <RetryImg
                        alt=""
                        src={url}
                        maxRetries={3}
                        retryDelayMs={1000}
                        style={{ 
                          width: 'auto', 
                          height: 'auto', 
                          maxWidth: imagesPerSlide === 2 ? '180px' : '250px',
                          maxHeight: imagesPerSlide === 2 ? '180px' : '250px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleImageClick(url)}
                      />
                    </Box>
                  ))}
                </Stack>
              </div>
            </Slide>
          ))}
        </Slider>
        <div className={styles.dotGroup}>
          {slides.map((_, index) => (
            <Dot
              key={index}
              disabled={false}
              slide={index}
              className={styles.dot}
            />
          ))}
        </div>
      </CarouselProvider>
    );
  };

  const renderStackedImages = () => {
    return (
      <Stack 
        direction="row" 
        spacing={2}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%'
        }}
      >
        {directImageUrls.map((url: string, index: number) => (
          <div 
            key={`${url}-${index}`} 
            className={styles.imageWrapper}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box 
              className={styles.imageWrapperInner}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <RetryImg
                alt=""
                src={url}
                maxRetries={3}
                retryDelayMs={1000}
                style={{ 
                  width: 'auto', 
                  height: 'auto', 
                  maxWidth: '200px',
                  maxHeight: '200px',
                  cursor: 'pointer' 
                }}
                onClick={() => handleImageClick(url)}
              />
            </Box>
          </div>
        ))}
      </Stack>
    );
  };

  return (
    <>
      {isMobile ? renderCarousel() : renderStackedImages()}
      
      {/* Модальное окно для увеличенного изображения */}
      <Modal
        open={!!selectedImage}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <RetryImg
              src={selectedImage || undefined}
              alt=""
              maxRetries={3}
              retryDelayMs={1000}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '90vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default ExampleImagesCarousel;
